import * as amplitude from "@amplitude/analytics-browser";
import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";
import { VariantData } from "../hooks/useFlow/use-flow";

export enum SuggestedExchangeEventType {
  VARIANT_EXCHANGE = "variant-exchange",
  EXCHANGE_GROUPS = "exchange-groups",
  RECOMMENDED_EXCHANGE = "recommended-exchange",
  NO_EXCHANGE = "no-exchange",
}

export type SuggestedExchangeEvent =
  | {
      type: SuggestedExchangeEventType.VARIANT_EXCHANGE;
      variantData: VariantData;
      suggestions: { name: string; values: string[] }[] | undefined;
    }
  | {
      type: SuggestedExchangeEventType.EXCHANGE_GROUPS;
      fromSuggestedPreview: boolean;
    }
  | { type: SuggestedExchangeEventType.RECOMMENDED_EXCHANGE; rank: number }
  | { type: SuggestedExchangeEventType.NO_EXCHANGE };

export function wasSuggestedVariant(
  variantData: { options: { name: string; value: string }[] },
  suggestions: { name: string; values: string[] }[] | undefined,
) {
  let wasSuggested = false;
  if (suggestions) {
    wasSuggested = variantData.options.some((option) => {
      const optionValue = option.value;
      const optionName = option.name;
      const optionSuggestion = suggestions.find(
        (suggestion) => suggestion.name === optionName,
      );
      if (optionSuggestion) {
        return optionSuggestion.values.includes(optionValue);
      }
      return false;
    });
  }
  return wasSuggested;
}

export interface SuggestedExchangeResult {
  exchangedItem: boolean;
  exchangeType: SuggestedExchangeEventType;
  additionalData: any;
}

const SuggestedExchangeResultZodSchema_ = z.object({
  exchangedItem: z.boolean(),
  exchangeType: z.enum(SuggestedExchangeEventType),
  additionalData: z.any(),
});
export const SuggestedExchangeResultZodSchema: z.ZodType<SuggestedExchangeResult> =
  SuggestedExchangeResultZodSchema_;
export interface SuggestedExchangeResultZod
  extends z.infer<typeof SuggestedExchangeResultZodSchema_> {}

export function getSuggestedExchangeResult(
  exchangeEvent: SuggestedExchangeEvent,
): SuggestedExchangeResult {
  let additionalData = undefined;
  switch (exchangeEvent.type) {
    case SuggestedExchangeEventType.VARIANT_EXCHANGE: {
      const variantData = exchangeEvent.variantData;
      const suggestions = exchangeEvent.suggestions;
      const wasSuggested = wasSuggestedVariant(variantData, suggestions);

      additionalData = { wasSuggested };
      break;
    }
    case SuggestedExchangeEventType.EXCHANGE_GROUPS: {
      additionalData = {
        fromSuggestedPreview: exchangeEvent.fromSuggestedPreview,
      };
      break;
    }
    case SuggestedExchangeEventType.NO_EXCHANGE: {
      break;
    }
    case SuggestedExchangeEventType.RECOMMENDED_EXCHANGE: {
      additionalData = { rank: exchangeEvent.rank };
      break;
    }
    default: {
      assertNever(exchangeEvent);
    }
  }
  return {
    exchangedItem:
      exchangeEvent.type !== SuggestedExchangeEventType.NO_EXCHANGE,
    exchangeType: exchangeEvent.type,
    additionalData,
  };
}

export function fireSuggestedExchangeEvent(
  suggestedExchangeResult: SuggestedExchangeResult,
) {
  amplitude.logEvent("suggested-exchange-result", suggestedExchangeResult);
}
